import React, { useEffect, useState } from 'react';
import { Pagination } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { PAGINATION_PAGE_SIZE } from 'utils/config';

type PaginationProps = {
  startCursor: string | null;
  endCursor: string | null;
  totalCount: number;
  onChange: (cursor: string | null) => void;
};

/** Simple cursor pagination component that allows the user to go back and forth the available pages. */
export const SimplePagination = ({
  startCursor,
  endCursor,
  totalCount,
  onChange,
}: PaginationProps) => {
  const [page, setPage] = useState<number>(1);
  const [currentStart, setCurrentStart] = useState<string | null>(startCursor);
  const [currentEnd, setCurrentEnd] = useState<string | null>(endCursor);
  const [allCursors, setAllCursors] = useState<(string | null)[]>([
    startCursor,
  ]);

  useEffect(() => {
    if (currentEnd !== endCursor) {
      setCurrentEnd(endCursor);
    }
  }, [startCursor, endCursor]);

  const handlePageChange = (newPage: number) => {
    let cursor: string | null = null;

    if (page === newPage) return;

    // Go forward with the cursor
    if (newPage > page) {
      cursor = currentEnd;
      setCurrentStart(currentEnd);
      setCurrentEnd(null);

      setAllCursors((state) => [...state, cursor]);
    }
    // Reset the cursor
    else if (newPage === 1) {
      cursor = null;
      setCurrentEnd(currentStart);
      setCurrentStart(null);

      setAllCursors([null]);
    }
    // Go back with the cursor
    else {
      cursor = allCursors[newPage - 1];
      setCurrentEnd(currentStart);
      setCurrentStart(cursor);

      allCursors.pop();
      setAllCursors([...allCursors]);
    }

    setPage(newPage);
    onChange(cursor);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGINATION_PAGE_SIZE));

  return (
    <StyledPagination>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(newPage: number) => handlePageChange(newPage)}
        hideItemsPerPage
        hideTeleport
      />
    </StyledPagination>
  );
};

const StyledPagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--cogs-greyscale-grey4);
`;
